import React, { useState } from 'react';
import { ChevronUp, ChevronDown, Download, Filter } from 'lucide-react';

/**
 * 司機績效表格組件
 * @param {Object} props
 * @param {Array} props.data - 表格數據
 * @param {boolean} props.loading - 是否正在加載
 * @param {function} props.onExport - 導出回調函數
 * @param {function} props.onRowClick - 行點擊回調函數
 */
const DriverPerformanceTable = ({
    data = [],
    loading = false,
    onExport,
    onRowClick
}) => {
    const [sortField, setSortField] = useState('total_trips');
    const [sortDirection, setSortDirection] = useState('desc');
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        minTrips: '',
        minRating: '',
        minAcceptance: ''
    });

    // 排序表格數據
    const sortedData = [...data].sort((a, b) => {
        let fieldA = a[sortField];
        let fieldB = b[sortField];

        // 處理數字類型
        if (typeof fieldA === 'number' && typeof fieldB === 'number') {
            return sortDirection === 'asc' ? fieldA - fieldB : fieldB - fieldA;
        }

        // 處理字符串類型
        if (typeof fieldA === 'string' && typeof fieldB === 'string') {
            return sortDirection === 'asc'
                ? fieldA.localeCompare(fieldB)
                : fieldB.localeCompare(fieldA);
        }

        return 0;
    });

    // 過濾表格數據
    const filteredData = sortedData.filter(driver => {
        if (filters.minTrips && driver.total_trips < Number(filters.minTrips)) {
            return false;
        }
        if (filters.minRating && driver.rating < Number(filters.minRating)) {
            return false;
        }
        if (filters.minAcceptance && driver.acceptance_rate < Number(filters.minAcceptance)) {
            return false;
        }
        return true;
    });

    // 處理排序點擊
    const handleSortClick = (field) => {
        if (field === sortField) {
            // 切換排序方向
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // 設定新的排序字段
            setSortField(field);
            setSortDirection('desc');
        }
    };

    // 渲染排序圖標
    const renderSortIcon = (field) => {
        if (field !== sortField) {
            return null;
        }

        return sortDirection === 'asc'
            ? <ChevronUp className="w-4 h-4" />
            : <ChevronDown className="w-4 h-4" />;
    };

    // 處理過濾變更
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // 重置過濾器
    const resetFilters = () => {
        setFilters({
            minTrips: '',
            minRating: '',
            minAcceptance: ''
        });
    };

    // 表頭配置
    const headers = [
        { field: 'full_name', label: '司機姓名' },
        { field: 'vehicle_info', label: '車輛資訊' },
        { field: 'total_trips', label: '總接單數' },
        { field: 'acceptance_rate', label: '接單率 (%)' },
        { field: 'cancellation_rate', label: '取消率 (%)' },
        { field: 'online_hours', label: '在線時數' },
        { field: 'rating', label: '評分' },
        { field: 'total_earnings', label: '總收入' }
    ];

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="p-6 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                    <h3 className="font-bold">司機績效</h3>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="px-3 py-2 border border-gray-300 rounded-md flex items-center text-sm"
                        >
                            <Filter className="w-4 h-4 mr-1" />
                            篩選
                        </button>
                        {onExport && (
                            <button
                                onClick={onExport}
                                className="px-3 py-2 border border-gray-300 rounded-md flex items-center text-sm"
                            >
                                <Download className="w-4 h-4 mr-1" />
                                導出
                            </button>
                        )}
                    </div>
                </div>

                {showFilters && (
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm text-gray-700 mb-1">
                                最低接單數
                            </label>
                            <input
                                type="number"
                                name="minTrips"
                                value={filters.minTrips}
                                onChange={handleFilterChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                placeholder="例：10"
                            />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-700 mb-1">
                                最低評分
                            </label>
                            <input
                                type="number"
                                name="minRating"
                                value={filters.minRating}
                                onChange={handleFilterChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                placeholder="例：4.5"
                                step="0.1"
                                min="1"
                                max="5"
                            />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-700 mb-1">
                                最低接單率 (%)
                            </label>
                            <input
                                type="number"
                                name="minAcceptance"
                                value={filters.minAcceptance}
                                onChange={handleFilterChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                placeholder="例：80"
                                min="0"
                                max="100"
                            />
                        </div>
                        <div className="md:col-span-3 flex justify-end">
                            <button
                                onClick={resetFilters}
                                className="text-blue-500 hover:underline text-sm"
                            >
                                重置篩選
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            {headers.map(header => (
                                <th
                                    key={header.field}
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                    onClick={() => handleSortClick(header.field)}
                                >
                                    <div className="flex items-center">
                                        {header.label}
                                        {renderSortIcon(header.field)}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredData.length === 0 ? (
                            <tr>
                                <td colSpan={headers.length} className="px-6 py-4 text-center text-gray-500">
                                    {data.length === 0 ? '無數據' : '無符合篩選條件的數據'}
                                </td>
                            </tr>
                        ) : (
                            filteredData.map((driver, index) => (
                                <tr
                                    key={driver.driver_id || index}
                                    className={onRowClick ? "hover:bg-gray-50 cursor-pointer" : ""}
                                    onClick={() => onRowClick && onRowClick(driver)}
                                >
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="w-8 h-8 bg-gray-200 rounded-full flex-shrink-0 mr-2">
                                                {driver.avatar_url && (
                                                    <img
                                                        src={driver.avatar_url}
                                                        alt={driver.full_name}
                                                        className="w-full h-full rounded-full object-cover"
                                                    />
                                                )}
                                            </div>
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">
                                                    {driver.full_name}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {driver.driver_id}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {driver.vehicle_model} • {driver.plate_number}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {driver.total_trips}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className={`text-sm font-medium ${driver.acceptance_rate >= 90 ? 'text-green-600' :
                                            driver.acceptance_rate >= 70 ? 'text-yellow-600' : 'text-red-600'
                                            }`}>
                                            {driver.acceptance_rate}%
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className={`text-sm font-medium ${driver.cancellation_rate <= 5 ? 'text-green-600' :
                                            driver.cancellation_rate <= 15 ? 'text-yellow-600' : 'text-red-600'
                                            }`}>
                                            {driver.cancellation_rate}%
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {driver.online_hours} 小時
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <svg className="w-4 h-4 text-yellow-400 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                            </svg>
                                            <span>{driver.rating.toFixed(1)}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        ${driver.total_earnings.toLocaleString()}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default DriverPerformanceTable;